/**
 * Chat client-state store (Zustand) — STORY-002-009.
 *
 * Holds the EPHEMERAL streaming state for the chat surface:
 *   - `activeThreadId`         the thread currently shown (null = new chat)
 *   - `isStreaming`            true between `send()` and the terminal event
 *   - `routingIntent`          the classifier intent once `event: intent`
 *                              arrives, before tokens start (AC #3)
 *   - `currentAssistantText`   the in-flight assistant message; tokens are
 *                              appended here as `event: token` frames arrive
 *   - `currentStage`           the pipeline step running RIGHT NOW (SCRUM-281);
 *                              cleared by any content frame
 *   - `awaitingFirstFrameSince` when the live turn began waiting for its first
 *                              frame; null once any frame arrived (drives the
 *                              «Соединяемся…» hint under the dots)
 *   - `streamError`            terminal error to render in the message slot
 *                              (not_implemented / rate_limited / network /
 *                              budget_exhausted) — AC #5/#6/#13
 *   - `abortController`        the AbortController for the live fetch so the
 *                              Stop button can cancel it (AC #7)
 *
 * This is CLIENT state only. Persisted thread messages come from TanStack
 * Query (`GET /chat/threads/{id}`, STORY-002-005). No persistence middleware
 * is wired — the access token lives in JS memory only and nothing here is
 * written to localStorage (REQ-PRD-§11.2-A, GUARDRAILS).
 *
 * i18n namespace registration:
 *   The `chat` namespace JSON lives under `src/locales/<lng>/chat.json`. The
 *   shared i18n bootstrap (`@/lib/i18n`) only preloads `common` + `auth`; per
 *   the feature-owns-its-namespace pattern this module registers the `chat`
 *   bundles via `addResourceBundle` at import time, which keeps the shared
 *   bootstrap untouched (it is outside this story's allowed-touch list) while
 *   making `useTranslation("chat")` resolve. RU/KY/EN are all first-class
 *   (ADR-028); RU is the documented fallback for any missing KY/EN key.
 */
import { create } from "zustand";

import i18n, { initI18n } from "@/lib/i18n";
import ruChat from "@/locales/ru/chat.json";
import kyChat from "@/locales/ky/chat.json";
import enChat from "@/locales/en/chat.json";
import type {
  CitationEvent,
  MaterialsEvent,
  Need,
  ProductCardData,
  SellerMatchCardData,
  StageEvent,
} from "./useChatStream";
import type { ClarifyRoundEvent } from "./clarify/types";
import type { ClarifyDraft } from "./clarify/draft";
import type { MessageAttachment } from "./attachment-types";
import type { GuestStreamErrorCode } from "@/features/chat/guest/allowance";

/**
 * Register the `chat` namespace on the shared i18n instance (idempotent).
 *
 * `initI18n()` is idempotent and ensures the instance is initialised before we
 * call `addResourceBundle` (the bundle methods only exist post-init). Calling
 * this from a feature module keeps the shared bootstrap (`@/lib/i18n`, outside
 * this story's allow-list) untouched while making `useTranslation("chat")`
 * resolve.
 */
export function registerChatI18n(): void {
  initI18n();
  if (!i18n.hasResourceBundle("ru", "chat")) {
    i18n.addResourceBundle("ru", "chat", ruChat, true, true);
  }
  if (!i18n.hasResourceBundle("ky", "chat")) {
    i18n.addResourceBundle("ky", "chat", kyChat, true, true);
  }
  if (!i18n.hasResourceBundle("en", "chat")) {
    i18n.addResourceBundle("en", "chat", enChat, true, true);
  }
}

registerChatI18n();

/**
 * Terminal stream-error shape. `code` is the backend `error` event's `code`
 * (or a synthetic client code for network failures); `intent` is carried for
 * the not_implemented banner; `retryAfterSeconds` is parsed for rate-limit.
 *
 * `rate_limited_user` / `rate_limited_ip` (bug-wave 2026-09-02) replace the
 * old single `rate_limited` code — the 429's `detail.code` from
 * `garden.chat.rate_limit` (`rate_limit.chat_user` / `rate_limit.chat_ip`)
 * now says WHICH sliding window tripped, and `useChatStream` maps it to one
 * of these two so the message slot can word the two cases truthfully (the
 * caller's own budget vs. an IP shared with others on the same network,
 * REQ-TRD-§14.6-A — never named or counted).
 *
 * Guest chat (SCRUM-273/274) adds the `guest_*` codes — the stream's names
 * for the contract's guest refusals; see `features/chat/guest/allowance.ts`.
 */
export interface ChatStreamError {
  code:
    | "not_implemented_in_mvp_sprint"
    | "rate_limited_user"
    | "rate_limited_ip"
    | "budget_exhausted"
    | "network_error"
    | "provider_error"
    | "session_expired"
    | "unknown"
    | GuestStreamErrorCode;
  intent?: string;
  retryAfterSeconds?: number;
}

/**
 * An in-memory turn for the CURRENT session. The persisted thread (TanStack
 * Query) is the durable source of truth once a thread id is known (existing
 * thread / STORY-002-010); for a brand-new chat the backend assigns the thread
 * id server-side and the UI does not yet learn it (out of scope here), so these
 * local turns keep the just-completed conversation visible (AC #2/#4).
 */
export interface LocalTurn {
  role: "user" | "assistant";
  content: string;
  /**
   * Product cards for an assistant product-search turn (demo slice). Carried on
   * the committed turn so the carousel survives between `done` and a thread
   * refetch (and across SPA navigation to the detail page and back).
   */
  cards?: ProductCardData[];
  /**
   * Quote `seller_match` cards for an assistant quote-review turn (design
   * revamp — the inline `SellerMiniCard` row). Committed onto the turn so the
   * seller mini-cards survive between `done` and a thread refetch.
   */
  sellerCards?: SellerMatchCardData[];
  /** The KB source articles this turn is grounded in (Phase 2 kb_used) — one
   * CitationChip per source. Empty/absent = the turn was ungrounded (no chip). */
  citations?: CitationEvent[];
  /** Suggested next questions for this turn (design follow-up pills), if any. */
  followups?: string[];
  /** The persisted assistant message id (from the `done` event) — enables the
   * thumbs feedback control on this just-streamed turn (Phase 5). */
  messageId?: string;
  /**
   * Attachments shown under a USER turn (chat document attachments). Carried on
   * the committed turn so the in-bubble image thumbs / file cards survive
   * between send and a thread refetch. Same shape as a rehydrated attachment.
   */
  attachments?: MessageAttachment[];
  /**
   * The sourced quote for an ASSISTANT attachment turn (the in-chat materials
   * table). Committed onto the turn so the table survives between `done` and a
   * thread refetch (and matches the rehydrated `{kind:"materials"}` cards_json
   * entry).
   */
  materials?: MaterialsEvent;
  /**
   * Recommendation Plan chips extracted from this turn's answer (Smart
   * Recommendations R1). Mutually exclusive with `followups` server-side —
   * committed onto the turn exactly like citations/followups so the chip row
   * survives a thread refetch and stays clickable after the stream ends.
   */
  plan?: Need[];
  /**
   * The clarifying-question round this turn asked (contract C8) — any kind.
   * Committed on `done`; the latest assistant turn's open round docks in
   * ClarifyCard (W3).
   */
  clarify?: ClarifyRoundEvent;
  /**
   * On a USER turn: the round whose answer this bubble summarises (W3, final
   * fix wave I3). The assistant turn after it is the answer, which offers no
   * Regenerate — re-sending the summary would post it as a plain chat message.
   */
  answersRound?: string;
  /**
   * When this turn happened, ISO-8601 (SCRUM-222). Stamped client-side at push
   * time: a local turn has no persisted row yet, and the whole point of the
   * timestamp is to be there WHILE the turn is still local. The persisted copy
   * carries the server's `created_at`, which wins once it arrives — this is
   * only ever the optimistic stand-in.
   */
  createdAt?: string;
}

export interface ChatState {
  activeThreadId: string | null;
  /**
   * The thread whose transcript this tab holds LOCALLY — adopted from the
   * server on `done` for a conversation that started as a new chat (W0.2).
   * ChatRoute does not fetch it while it is the open thread: the local turns
   * already are the transcript (and some `done` frames carry no `message_id`,
   * so a refetch could not dedupe them). A reload — fresh store, null here —
   * restores it from the server through `?t=`. Any explicit switch clears it.
   */
  localThreadId: string | null;
  isStreaming: boolean;
  routingIntent: string | null;
  currentAssistantText: string;
  /**
   * In-flight product cards for the CURRENT product-search turn (demo slice),
   * set from `event: products` before/alongside the lead-in token. Committed
   * onto the assistant `LocalTurn` and cleared on `done`.
   */
  currentProductCards: ProductCardData[];
  /**
   * In-flight quote `seller_match` cards for the CURRENT quote-review turn
   * (design revamp), set from the `products` event. Committed onto the
   * assistant `LocalTurn` and cleared on `done`.
   */
  currentSellerCards: SellerMatchCardData[];
  /** The KB sources the CURRENT turn is grounded in — accumulated (de-duped by
   * slug) from the `retrieval` kb_used[] and per-source `citation` events. */
  currentCitations: CitationEvent[];
  /** Suggested next questions for the CURRENT turn (`event: followups`). */
  currentFollowups: string[];
  /**
   * The pipeline step the server says is running RIGHT NOW (SCRUM-281,
   * `event: stage`). Cleared by every frame that renders content — a token,
   * cards, the materials table, a clarify question, a plan, follow-ups — so a
   * stage can never outlive the step it names; reset with the rest of the
   * turn's scratch state. `null` before the first stage arrives.
   */
  currentStage: StageEvent | null;
  /**
   * When (epoch ms) the live turn began waiting for its FIRST frame — set by
   * `beginStream`, cleared for good by the first frame of any type
   * (`markStreamFrame`) and by every path that ends or abandons the turn.
   * Null otherwise. While it is set and 4 s have passed, the dots carry the
   * «Соединяемся…» hint (owner-approved 2026-10-13): honest, because it only
   * shows while nothing at all has arrived for this turn.
   */
  awaitingFirstFrameSince: number | null;
  /**
   * The in-flight sourced quote for the CURRENT attachment turn
   * (`event: materials`). Committed onto the assistant `LocalTurn` and cleared
   * on `done`.
   */
  currentMaterials: MaterialsEvent | null;
  /**
   * In-flight Recommendation Plan for the CURRENT turn (`event: plan`, Smart
   * Recommendations R1). Mutually exclusive with `currentFollowups`.
   */
  currentPlan: Need[];
  /**
   * The in-flight round for the CURRENT turn (`event: clarify`, contract C8).
   * Turn-scoped: every reset clears it; it reaches the transcript only through
   * `commitAssistantTurn`, so the card never docks mid-stream.
   */
  currentClarify: ClarifyRoundEvent | null;
  /**
   * `need_id`s already fetched (a chip tap OR a typed turn the orchestrator
   * resolved against the plan) in the ACTIVE thread (Smart Recommendations
   * R1) — mirrors the backend `explored_need_ids_for_thread`. Thread-scoped:
   * reset on thread switch, seeded from the rehydrated derivation
   * (`plan-rehydrate.ts`) on a reload.
   */
  exploredNeedIds: Record<string, true>;
  /**
   * Transient per-need fetch UI status for the plan chips (Smart
   * Recommendations R1) — `"loading"` while its `fetch_need` call is in
   * flight, `"error"` after a failed fetch (inline retry — tap the chip
   * again). Absent = idle. Thread-scoped; reset on thread switch.
   */
  needFetchStatus: Record<string, "loading" | "error">;
  /**
   * Rounds closed on THIS client (W3): submitted, dismissed or picked. A closed
   * round never re-docks from its committed local turn or a stale persisted
   * copy; `reopenClarifyRound` undoes it when an answer failed before its
   * first token (the server reopened the round). Thread-scoped, like
   * `exploredNeedIds`.
   */
  closedClarifyRounds: Record<string, true>;
  /**
   * The open round's draft — memory only (spec: drafts live in Zustand; a
   * reload starts empty). One at a time: `draftFor` ignores a draft that
   * belongs to another round. Kept through a submission, so a round that
   * re-docks after a failed answer still holds the user's choices.
   */
  clarifyDraft: ClarifyDraft | null;
  streamError: ChatStreamError | null;
  abortController: AbortController | null;
  localTurns: LocalTurn[];
  /**
   * Per-thread scroll offset (px from top of the message list), keyed by
   * threadId (STORY-002-010 AC#5). Caching the previous thread's offset and
   * restoring it on switch is the "preserves cursor between threads" behaviour
   * from the EPIC-002 README. Client-only; never persisted (no localStorage).
   */
  scrollPositions: Record<string, number>;
  /**
   * Bumped by every `resetAll()` (W2.6, ruling P7a). A reset can run while
   * ChatRoute stays mounted — another tab ended the guest session, the 409
   * claim outcome, a persistent 401 before its page redirect — and ChatRoute
   * keeps per-conversation refs this store cannot reach (the server thread of
   * a fresh chat, the failed-retry text, a pending switch). It watches this
   * counter to drop them together with the store.
   */
  resetEpoch: number;

  setActiveThreadId: (id: string | null) => void;
  /**
   * Adopt the server-derived thread id of a brand-new chat once its first
   * turn is persisted (`done`), WITHOUT the reset `setActiveThreadId`
   * performs — the conversation on screen IS that thread (W0.2).
   */
  adoptServerThread: (threadId: string) => void;
  /**
   * Back to a fresh tab: aborts a live stream and resets every field
   * (logout, idle logout, persistent 401, guest session end — W0.3/W2.6),
   * then bumps `resetEpoch` so a mounted ChatRoute drops its own refs too.
   */
  resetAll: () => void;
  /** Cache the scroll offset for a thread (STORY-002-010 AC#5). */
  setScrollPosition: (threadId: string, offset: number) => void;
  /** Push the user's message as a local turn (rendered immediately on send).
   * `attachments` (chat document attachments) render under the user bubble;
   * `answersRound` marks a round's answer summary (see `LocalTurn`). */
  pushUserTurn: (
    content: string,
    attachments?: MessageAttachment[],
    options?: { answersRound?: string },
  ) => void;
  /**
   * Drop the LAST local turn iff it is an id-less user turn with exactly this
   * text — a guest send the server refused before any LLM work (W2.4a). The
   * message was never accepted, so its bubble must not stay as an orphan.
   */
  popPendingUserTurn: (content: string) => void;
  /** Begin a new stream: arms the abort controller, clears prior state. */
  beginStream: (controller: AbortController) => void;
  /** A frame of any type arrived on the live primary stream: the turn is no
   * longer waiting for its first one. Called per frame; a no-op (no
   * re-render) after the first. */
  markStreamFrame: () => void;
  /** Record the classifier decision (`event: intent`). */
  setRoutingIntent: (intent: string) => void;
  /** Set the in-flight product cards (`event: products`). */
  setProductCards: (cards: ProductCardData[]) => void;
  /** Set the in-flight quote `seller_match` cards (`event: products`). */
  setSellerCards: (cards: SellerMatchCardData[]) => void;
  /** Add KB sources for the CURRENT turn (`retrieval` kb_used + `citation`
   * events), de-duped by slug. */
  addCitations: (citations: CitationEvent[]) => void;
  /** Set the in-flight follow-up questions (`event: followups`). */
  setFollowups: (items: string[]) => void;
  /** Record the step that just started (`event: stage`; also the reading
   * stage for `event: attachment_status`). */
  setStage: (stage: StageEvent) => void;
  /** Set the in-flight sourced quote (`event: materials`). */
  setMaterials: (materials: MaterialsEvent) => void;
  /** Set the in-flight Recommendation Plan (`event: plan`). */
  setPlan: (items: Need[]) => void;
  /** Set (or clear) the in-flight round (`event: clarify`). */
  setClarify: (event: ClarifyRoundEvent | null) => void;
  /** Replace the active thread's explored-Need set wholesale — used to seed
   * the rehydrated derivation (`plan-rehydrate.ts`) on a thread load. */
  setExploredNeedIds: (ids: Iterable<string>) => void;
  /** Union `ids` into the active thread's explored-Need set (a live
   * Need-scoped `products` event on the PRIMARY stream — a typed turn the
   * orchestrator resolved against the plan). */
  mergeExploredNeedIds: (ids: Iterable<string>) => void;
  /** Mark ONE Need's fetch in flight (a chip tap / clarify pick / "show all"
   * step) — clears any previous error for it. */
  beginNeedFetch: (needId: string) => void;
  /** Mark ONE Need's fetch as successfully completed (explored) — regardless
   * of whether any cards were returned (honest exhaustion is still
   * "explored", mirroring the backend's `explored_need_ids_for_thread`). */
  completeNeedFetch: (needId: string) => void;
  /** Mark ONE Need's fetch as failed (inline retry — tap the chip again). */
  failNeedFetch: (needId: string) => void;
  /** Return ONE Need's chip to idle — neither failed nor explored. For a
   * fetch that never got an answer and is no error either: a guest's tap
   * refused at the limit opens the sign-up sheet instead (SCRUM-274). */
  clearNeedFetch: (needId: string) => void;
  /** Close a round locally (submitted, dismissed or picked). */
  closeClarifyRound: (roundId: string) => void;
  /** Re-open a round the server reopened (an answer that failed before its first token). */
  reopenClarifyRound: (roundId: string) => void;
  /** Replace the in-memory draft (ClarifyCard's `onDraftChange`). */
  setClarifyDraft: (draft: ClarifyDraft | null) => void;
  /**
   * Append a completed Need-fetch turn (a chip tap / clarify pick / "show
   * all" step) directly to `localTurns` — a `fetch_need` action carries no
   * typed text and runs on a DEDICATED stream instance, so it never goes
   * through `pushUserTurn`/`commitAssistantTurn` (the PRIMARY turn's scratch
   * state must stay untouched by a chip tap).
   */
  pushNeedFetchTurn: (
    content: string,
    cards: ProductCardData[],
    messageId?: string,
  ) => void;
  /** Append one streamed token to the in-flight assistant message. */
  appendToken: (value: string) => void;
  /** Commit the accumulated assistant text as a local turn (on `done`).
   *
   * `messageId` (from the done event) enables the turn's feedback control AND
   * lets `MessageList` retire this optimistic copy once the persisted row
   * arrives. `userMessageId` does the same for the optimistic USER bubble
   * pushed by `pushUserTurn` — without it that bubble could never be retired,
   * so after the post-`done` refetch it rendered BELOW the persisted answer and
   * the user saw the reply above their own question (SCRUM-238). */
  commitAssistantTurn: (messageId?: string, userMessageId?: string) => void;
  /** Terminal failure — renders an error slot + (caller fires) a toast. */
  setStreamError: (error: ChatStreamError) => void;
  /** Clear the in-flight assistant message (commit/abort/reset). */
  resetStreaming: () => void;
  /** Stop the live stream (AC #7): abort the fetch, discard the partial. */
  stopStreaming: () => void;
}

/** Every data field's initial value — shared by the store and `resetAll`. */
const INITIAL_CHAT_STATE = {
  activeThreadId: null,
  localThreadId: null,
  isStreaming: false,
  routingIntent: null,
  currentAssistantText: "",
  currentProductCards: [],
  currentSellerCards: [],
  currentCitations: [],
  currentFollowups: [],
  currentStage: null,
  awaitingFirstFrameSince: null,
  currentMaterials: null,
  currentPlan: [],
  currentClarify: null,
  exploredNeedIds: {},
  needFetchStatus: {},
  closedClarifyRounds: {},
  clarifyDraft: null,
  streamError: null,
  abortController: null,
  localTurns: [],
  scrollPositions: {},
} satisfies Partial<ChatState>;

export const useChatStore = create<ChatState>((set, get) => ({
  ...INITIAL_CHAT_STATE,
  resetEpoch: 0,

  setActiveThreadId: (id) =>
    set({
      activeThreadId: id,
      localThreadId: null,
      // Switching / starting threads discards in-flight + local session state.
      isStreaming: false,
      routingIntent: null,
      currentAssistantText: "",
      currentProductCards: [],
      currentSellerCards: [],
      currentCitations: [],
      currentFollowups: [],
      currentStage: null,
      awaitingFirstFrameSince: null,
      currentMaterials: null,
      currentPlan: [],
      currentClarify: null,
      // Smart Recommendations R1 state is THREAD-scoped (mirrors the backend
      // `explored_need_ids_for_thread`) — a thread switch resets it; the
      // ChatRoute effect re-seeds it from the rehydrated derivation once the
      // newly-active thread's messages load.
      exploredNeedIds: {},
      needFetchStatus: {},
      // Clarify rounds belong to the thread on screen, like the plan state.
      closedClarifyRounds: {},
      clarifyDraft: null,
      streamError: null,
      localTurns: [],
    }),

  adoptServerThread: (threadId) =>
    set({ activeThreadId: threadId, localThreadId: threadId }),

  resetAll: () => {
    const { abortController, resetEpoch } = get();
    if (abortController !== null) abortController.abort();
    set({ ...INITIAL_CHAT_STATE, resetEpoch: resetEpoch + 1 });
  },

  setScrollPosition: (threadId, offset) =>
    set((state) => ({
      scrollPositions: { ...state.scrollPositions, [threadId]: offset },
    })),

  pushUserTurn: (content, attachments, options) =>
    set((state) => {
      const turn: LocalTurn = {
        role: "user",
        content,
        createdAt: new Date().toISOString(),
      };
      if (attachments !== undefined && attachments.length > 0) {
        turn.attachments = attachments;
      }
      if (options?.answersRound !== undefined) {
        turn.answersRound = options.answersRound;
      }
      return { localTurns: [...state.localTurns, turn] };
    }),

  popPendingUserTurn: (content) =>
    set((state) => {
      const last = state.localTurns[state.localTurns.length - 1];
      if (
        last === undefined ||
        last.role !== "user" ||
        last.messageId !== undefined ||
        last.content !== content
      ) {
        // The unchanged `state` object (not `{}`): Zustand skips notifying
        // subscribers when the updater returns the SAME reference it was
        // given, so a no-op here causes no re-render (fix round 1).
        return state;
      }
      return { localTurns: state.localTurns.slice(0, -1) };
    }),

  beginStream: (controller) =>
    set({
      isStreaming: true,
      routingIntent: null,
      currentAssistantText: "",
      currentProductCards: [],
      currentSellerCards: [],
      currentCitations: [],
      currentFollowups: [],
      currentStage: null,
      // The clock for «Соединяемся…» starts with the turn.
      awaitingFirstFrameSince: Date.now(),
      currentMaterials: null,
      // Turn-scoped (unlike exploredNeedIds/needFetchStatus, which are
      // thread-scoped and survive across turns).
      currentPlan: [],
      currentClarify: null,
      streamError: null,
      abortController: controller,
    }),

  // Returning the SAME state when the clock is already stopped makes Zustand
  // skip notifying subscribers, so a token-per-frame stream re-renders nothing.
  markStreamFrame: () =>
    set((state) =>
      state.awaitingFirstFrameSince === null ? state : { awaitingFirstFrameSince: null },
    ),

  setRoutingIntent: (intent) => set({ routingIntent: intent }),

  setProductCards: (cards) =>
    set({ currentProductCards: cards, currentStage: null }),

  setSellerCards: (cards) =>
    set({ currentSellerCards: cards, currentStage: null }),

  addCitations: (citations) =>
    set((state) => {
      const seen = new Set(state.currentCitations.map((c) => c.slug));
      const added = citations.filter((c) => c.slug && !seen.has(c.slug));
      return added.length > 0
        ? { currentCitations: [...state.currentCitations, ...added] }
        : {};
    }),

  setFollowups: (items) => set({ currentFollowups: items, currentStage: null }),

  setStage: (stage) => set({ currentStage: stage }),

  setMaterials: (materials) =>
    set({ currentMaterials: materials, currentStage: null }),

  setPlan: (items) => set({ currentPlan: items, currentStage: null }),

  // Clearing the slot is not content; only a real question replaces the stage.
  setClarify: (event) =>
    set(
      event === null
        ? { currentClarify: null }
        : { currentClarify: event, currentStage: null },
    ),

  setExploredNeedIds: (ids) =>
    set({
      exploredNeedIds: Object.fromEntries(
        [...ids].map((id) => [id, true as const]),
      ),
    }),

  mergeExploredNeedIds: (ids) =>
    set((state) => {
      const next = { ...state.exploredNeedIds };
      let changed = false;
      for (const id of ids) {
        if (!next[id]) {
          next[id] = true;
          changed = true;
        }
      }
      return changed ? { exploredNeedIds: next } : {};
    }),

  beginNeedFetch: (needId) =>
    set((state) => {
      const status = { ...state.needFetchStatus };
      status[needId] = "loading";
      return { needFetchStatus: status };
    }),

  completeNeedFetch: (needId) =>
    set((state) => {
      const status = { ...state.needFetchStatus };
      delete status[needId];
      return {
        needFetchStatus: status,
        exploredNeedIds: { ...state.exploredNeedIds, [needId]: true },
      };
    }),

  failNeedFetch: (needId) =>
    set((state) => ({
      needFetchStatus: { ...state.needFetchStatus, [needId]: "error" },
    })),

  clearNeedFetch: (needId) =>
    set((state) => {
      const status = { ...state.needFetchStatus };
      delete status[needId];
      return { needFetchStatus: status };
    }),

  closeClarifyRound: (roundId) =>
    set((state) =>
      state.closedClarifyRounds[roundId] === true
        ? state
        : { closedClarifyRounds: { ...state.closedClarifyRounds, [roundId]: true } },
    ),

  reopenClarifyRound: (roundId) =>
    set((state) => {
      if (state.closedClarifyRounds[roundId] !== true) return state;
      const next = { ...state.closedClarifyRounds };
      delete next[roundId];
      return { closedClarifyRounds: next };
    }),

  setClarifyDraft: (draft) => set({ clarifyDraft: draft }),

  pushNeedFetchTurn: (content, cards, messageId) =>
    set((state) => {
      const turn: LocalTurn = {
        role: "assistant",
        content,
        createdAt: new Date().toISOString(),
      };
      if (cards.length > 0) turn.cards = cards;
      // Without an id this optimistic copy survives the persisted one and the
      // chip's reply renders twice (SCRUM-188). Absent id = the server did not
      // persist the turn, and the local copy is then the only copy — keep it.
      if (messageId !== undefined && messageId.length > 0) {
        turn.messageId = messageId;
      }
      return { localTurns: [...state.localTurns, turn] };
    }),

  appendToken: (value) =>
    set((state) => ({
      currentAssistantText: state.currentAssistantText + value,
      // Text arrived: whatever stage was showing has finished (SCRUM-281).
      currentStage: null,
    })),

  commitAssistantTurn: (messageId, userMessageId) =>
    set((state) => {
      // Retire the optimistic USER bubble by stamping it with the id the
      // server just persisted. Only the most recent un-stamped user turn is
      // touched: earlier ones already carry their own ids, and a turn that
      // never got persisted must keep rendering (it is the only copy).
      //
      // Without this the bubble was permanently id-less, so `MessageList`'s
      // dedupe could not retire it and it rendered after the persisted answer
      // — the "answer above the question" report (SCRUM-238).
      let localTurns = state.localTurns;
      if (userMessageId !== undefined && userMessageId.length > 0) {
        // Hand-rolled reverse scan rather than `findLastIndex`: the web app
        // targets ES2022 (tsconfig `target`/`lib`), where that method is not
        // declared. Bumping the whole app's lib for one call is not a trade
        // this fix should make.
        let target = -1;
        for (let i = localTurns.length - 1; i >= 0; i -= 1) {
          const candidate = localTurns[i];
          if (candidate?.role === "user" && candidate.messageId === undefined) {
            target = i;
            break;
          }
        }
        if (target !== -1) {
          localTurns = localTurns.map((turn, index) =>
            index === target ? { ...turn, messageId: userMessageId } : turn,
          );
        }
      }

      const text = state.currentAssistantText;
      const cards = state.currentProductCards;
      const sellerCards = state.currentSellerCards;
      const citations = state.currentCitations;
      const followups = state.currentFollowups;
      const materials = state.currentMaterials;
      const plan = state.currentPlan;
      const clarify = state.currentClarify;
      // Commit a turn when there is text OR any cards OR a sourced quote (an
      // attachment turn streams a lead-in + the materials event).
      //
      // Contract C11: a round turn persists "the lead, or the first question's
      // text" as its content. A smart-rec round streams no lead, so the local
      // copy takes the same text — the reply reads the same before and after
      // the post-`done` refetch replaces it.
      const turn: LocalTurn = {
        role: "assistant",
        content: text.length > 0 ? text : (clarify?.questions[0]?.text ?? ""),
        createdAt: new Date().toISOString(),
      };
      if (cards.length > 0) turn.cards = cards;
      if (sellerCards.length > 0) turn.sellerCards = sellerCards;
      if (citations.length > 0) turn.citations = citations;
      if (followups.length > 0) turn.followups = followups;
      if (materials !== null) turn.materials = materials;
      if (plan.length > 0) turn.plan = plan;
      if (clarify !== null) turn.clarify = clarify;
      if (messageId !== undefined && messageId.length > 0) {
        turn.messageId = messageId;
      }
      // A turn is worth keeping if it carries ANYTHING renderable — which
      // includes a clarify question or a plan, not just prose and cards.
      //
      // `clarify` and `plan` were missing here, and it silently destroyed the
      // R1 §3.2b flow: a plan-clarify turn streams ONLY `clarify` + `done`, so
      // `turn.clarify` was assembled four lines above and then dropped with
      // the turn. The user saw nothing at all — no error, no empty bubble —
      // for a question the server had already composed AND persisted. Found
      // live 2026-07-29, reported as "no response, AI just broke off".
      //
      // The guard itself still matters: an aborted or no-op stream must not
      // leave an empty bubble, which is why this widens rather than goes away.
      const hasContent =
        text.length > 0 ||
        cards.length > 0 ||
        sellerCards.length > 0 ||
        materials !== null ||
        clarify !== null ||
        plan.length > 0;
      // `localTurns`, NOT `state.localTurns` — the former carries the user
      // bubble's freshly-stamped id from the top of this reducer, and reading
      // through `state` here would silently throw that stamp away.
      const turns = hasContent ? [...localTurns, turn] : localTurns;
      return {
        localTurns: turns,
        isStreaming: false,
        routingIntent: null,
        currentAssistantText: "",
        currentProductCards: [],
        currentSellerCards: [],
        currentCitations: [],
        currentFollowups: [],
        currentStage: null,
        awaitingFirstFrameSince: null,
        currentMaterials: null,
        currentPlan: [],
        currentClarify: null,
        abortController: null,
      };
    }),

  setStreamError: (error) =>
    set({
      streamError: error,
      isStreaming: false,
      currentProductCards: [],
      currentSellerCards: [],
      currentCitations: [],
      currentFollowups: [],
      currentStage: null,
      awaitingFirstFrameSince: null,
      currentMaterials: null,
      currentPlan: [],
      currentClarify: null,
      abortController: null,
    }),

  resetStreaming: () =>
    set({
      isStreaming: false,
      routingIntent: null,
      currentAssistantText: "",
      currentProductCards: [],
      currentSellerCards: [],
      currentCitations: [],
      currentFollowups: [],
      currentStage: null,
      awaitingFirstFrameSince: null,
      currentMaterials: null,
      currentPlan: [],
      currentClarify: null,
      abortController: null,
    }),

  stopStreaming: () => {
    const { abortController } = get();
    if (abortController !== null) {
      abortController.abort();
    }
    // Discard the partial message and re-enable the input (AC #7).
    set({
      isStreaming: false,
      routingIntent: null,
      currentAssistantText: "",
      currentProductCards: [],
      currentSellerCards: [],
      currentCitations: [],
      currentFollowups: [],
      currentStage: null,
      awaitingFirstFrameSince: null,
      currentMaterials: null,
      currentPlan: [],
      currentClarify: null,
      abortController: null,
    });
  },
}));
